import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import type { StoryDto, StoryGroupDto } from '@tg-hookah/shared/dto';
import { Spinner } from '@/components/Spinner';

interface Props {
  groups: StoryGroupDto[];
  startIndex: number;
  onClose: () => void;
  onGroupViewed?: (groupId: string) => void;
}

/** Preload the media behind a story so advancing to it is instant. Images go
 *  through the browser cache via `new Image()`; videos via a detached element. */
function preload(story: StoryDto | undefined): void {
  if (!story) return;
  if (story.mediaType === 'VIDEO') {
    const v = document.createElement('video');
    v.preload = 'auto';
    v.src = story.mediaUrl;
  } else {
    const img = new Image();
    img.src = story.mediaUrl;
  }
}

export function StoryViewer({ groups, startIndex, onClose, onGroupViewed }: Props) {
  const [groupIdx, setGroupIdx] = useState(startIndex);
  const [storyIdx, setStoryIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  // Gate the progress timer on the current media being decoded/buffered, so the
  // bar doesn't run over a blank frame — the thing that felt "нединамично".
  const [ready, setReady] = useState(false);
  const startY = useRef<number | null>(null);

  const group = groups[groupIdx];
  const story = group?.stories[storyIdx];

  useEffect(() => setStoryIdx(0), [groupIdx]);

  // Reset the loading gate whenever the visible media changes.
  useEffect(() => setReady(false), [story?.id]);

  // Viewing a group's first content item marks the whole group viewed.
  useEffect(() => {
    if (group) onGroupViewed?.(group.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id]);

  // The next media item, used for prefetching so the jump is seamless.
  const nextStory = useMemo<StoryDto | undefined>(() => {
    if (!group) return undefined;
    if (storyIdx + 1 < group.stories.length) return group.stories[storyIdx + 1];
    return groups[groupIdx + 1]?.stories[0];
  }, [groups, group, groupIdx, storyIdx]);

  // Prefetch the next item as soon as the current one is on screen.
  useEffect(() => {
    if (ready) preload(nextStory);
  }, [ready, nextStory]);

  useEffect(() => {
    if (!story) return;
    setProgress(0);
    // Hold the timer until the media is ready (or errored) and not paused.
    if (paused || !ready) return;
    const duration = (story.durationSec || 5) * 1000;
    const stepMs = 50;
    let elapsed = 0;
    const tick = window.setInterval(() => {
      elapsed += stepMs;
      const pct = Math.min(1, elapsed / duration);
      setProgress(pct);
      if (pct >= 1) {
        window.clearInterval(tick);
        advance();
      }
    }, stepMs);
    return () => window.clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.id, paused, ready]);

  function advance() {
    if (!group) return;
    if (storyIdx + 1 < group.stories.length) {
      setStoryIdx(storyIdx + 1);
    } else if (groupIdx + 1 < groups.length) {
      setGroupIdx(groupIdx + 1);
    } else {
      onClose();
    }
  }

  function regress() {
    if (storyIdx > 0) setStoryIdx(storyIdx - 1);
    else if (groupIdx > 0) setGroupIdx(groupIdx - 1);
  }

  function handleTap(e: React.MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < rect.width / 3) regress();
    else advance();
  }

  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    startY.current = e.clientY;
    setPaused(true);
  }
  function onPointerUp(e: PointerEvent<HTMLDivElement>) {
    setPaused(false);
    const sy = startY.current;
    startY.current = null;
    if (sy != null && e.clientY - sy > 80) onClose();
  }

  if (!group || !story) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black text-white flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={group.title}
    >
      <div className="flex gap-1 p-2 pt-3">
        {group.stories.map((s, i) => (
          <div key={s.id} className="flex-1 h-1 rounded-full bg-white/20 overflow-hidden">
            <div
              className="h-full bg-white"
              style={{
                width: i < storyIdx ? '100%' : i === storyIdx ? `${progress * 100}%` : '0%',
              }}
            />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between px-3 pb-2">
        <div className="text-sm font-medium truncate">{group.title}</div>
        <button
          type="button"
          onClick={onClose}
          aria-label="close"
          className="w-9 h-9 rounded-full bg-white/15 inline-flex items-center justify-center"
        >
          ×
        </button>
      </div>
      <div
        className="flex-1 relative select-none"
        onClick={handleTap}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
      >
        {story.mediaType === 'VIDEO' ? (
          <video
            key={story.id}
            src={story.mediaUrl}
            autoPlay
            playsInline
            controls={false}
            preload="auto"
            className="w-full h-full object-contain"
            onLoadedData={() => setReady(true)}
            onError={() => setReady(true)}
          />
        ) : (
          <img
            key={story.id}
            src={story.mediaUrl}
            alt=""
            className="w-full h-full object-contain"
            onLoad={() => setReady(true)}
            // A broken/slow-failing URL must not freeze the viewer: let the
            // timer run so the story still advances.
            onError={() => setReady(true)}
          />
        )}
        {!ready ? (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Spinner className="text-white/80 w-8 h-8" />
          </div>
        ) : null}
        {story.ctaText && story.ctaUrl ? (
          <a
            href={story.ctaUrl}
            target="_blank"
            rel="noopener"
            className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white text-black rounded-full px-5 py-2 text-[12px] font-display uppercase tracking-[0.08em] inline-flex items-center gap-2"
            style={{ boxShadow: '0 10px 24px -12px rgba(255,255,255,0.4)' }}
          >
            {story.ctaText}
            <span aria-hidden className="font-mono opacity-70">↗</span>
          </a>
        ) : null}
      </div>
    </div>
  );
}
